/**
 * Lodestar — Cookie consent banner.
 *
 * Tier 1 product readiness: GDPR / ePrivacy compliance. The banner
 * blocks the bottom of the screen until the visitor either accepts or
 * declines analytics cookies. Choice is stored to localStorage so the
 * banner doesn't re-appear on subsequent visits.
 *
 * PostHog opt-out wiring:
 *   • On "Accept all"   → window.posthog.opt_in_capturing()
 *   • On "Reject"       → window.posthog.opt_out_capturing()
 *   • If no choice yet  → PostHog is loaded but quiet (the snippet in
 *                          index.html boots in `opt_out` mode? Not in
 *                          ours — so we explicitly opt the user out
 *                          on first paint until they accept).
 *
 * The banner is intentionally minimal: no granular toggles ("strictly
 * necessary" / "analytics" / "marketing") because Lodestar only loads
 * one analytics tool. If you add Meta Pixel / GTM later, evolve to a
 * full CMP at that time.
 */
import { useCallback, useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "lodestar.cookie_consent";  // "accepted" | "declined"

function readChoice() {
    try {
        if (typeof window === "undefined") return null;
        return window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function writeChoice(v) {
    try {
        window.localStorage.setItem(STORAGE_KEY, v);
    } catch {
        // localStorage blocked → choice doesn't persist, banner re-appears.
    }
}

function applyPostHogChoice(choice) {
    try {
        if (typeof window === "undefined" || !window.posthog) return;
        if (choice === "accepted") {
            window.posthog.opt_in_capturing?.();
        } else if (choice === "declined") {
            window.posthog.opt_out_capturing?.();
        }
    } catch {
        // Analytics must never break the app.
    }
}

function applyGtagChoice(choice) {
    // Google Consent Mode v2 — flip analytics_storage to match.
    // The default ('denied') is set in public/index.html before any
    // pageview fires, so we only ever UPGRADE here on "accepted".
    try {
        if (typeof window === "undefined" || typeof window.gtag !== "function") return;
        window.gtag("consent", "update", {
            analytics_storage: choice === "accepted" ? "granted" : "denied",
        });
    } catch {
        // ignore
    }
}

function applyChoice(choice) {
    applyPostHogChoice(choice);
    applyGtagChoice(choice);
}

export default function CookieConsentBanner() {
    const [choice, setChoice] = useState(() => readChoice());
    const [visible, setVisible] = useState(false);

    // Delay the banner show by 600ms so it doesn't compete with the
    // first paint, then ensure PostHog is in the correct state on every
    // mount.
    useEffect(() => {
        applyChoice(choice);
        if (!choice) {
            // Explicit opt-out until user decides — defensive default.
            applyChoice("declined");
            const t = window.setTimeout(() => setVisible(true), 600);
            return () => window.clearTimeout(t);
        }
    }, [choice]);

    const accept = useCallback(() => {
        writeChoice("accepted");
        applyChoice("accepted");
        setChoice("accepted");
        setVisible(false);
    }, []);

    const decline = useCallback(() => {
        writeChoice("declined");
        applyChoice("declined");
        setChoice("declined");
        setVisible(false);
    }, []);

    // Suppress the banner on the Admin portal — operators are signed-in
    // internal users (not anonymous visitors) and the banner overlays the
    // bottom-right corner of /admin/kp where the kp-reasoning-run primary
    // CTA also sits, causing accidental click interception. GDPR posture
    // for admins is handled by their employment/SOC2 controls, not by an
    // anonymous-consent cookie modal.
    const isAdminRoute = (() => {
        try {
            return typeof window !== "undefined"
                && window.location.pathname.startsWith("/admin");
        } catch { return false; }
    })();

    if (!visible || choice || isAdminRoute) return null;

    return (
        <div
            data-testid="cookie-consent-banner"
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            className="fixed bottom-0 left-0 right-0 sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-md z-[9999]
                       border-t border-lodestar-gold-soft/30
                       sm:border sm:border-lodestar-gold-soft/40 sm:rounded-2xl
                       bg-lodestar-plum-deep/95 backdrop-blur-xl shadow-2xl"
        >
            {/* Mobile: slim single-row bar so it never covers the primary CTA. */}
            {/* Desktop (sm+): full card with detailed copy on the side. */}
            <div className="px-4 py-3 sm:p-6">
                <div className="flex items-center gap-3 sm:items-start">
                    <div className="hidden sm:grid w-9 h-9 rounded-md place-items-center bg-lodestar-gold-royal/14 border border-lodestar-gold-soft/35 text-lodestar-gold-bright shrink-0">
                        <Cookie className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        {/* Mobile: one-line summary */}
                        <p className="sm:hidden text-[12.5px] text-lodestar-white-mist/85 leading-snug">
                            We use cookies for analytics. <a href="/privacy" className="text-lodestar-gold-bright underline">Privacy</a>.
                        </p>
                        {/* Desktop: full copy */}
                        <div className="hidden sm:block font-display text-[16px] text-lodestar-white-pure leading-snug">
                            A small thing about cookies.
                        </div>
                        <p className="hidden sm:block text-[13px] text-lodestar-white-mist/80 leading-[1.65] mt-2">
                            Lodestar uses a single analytics tool (PostHog) to understand which features help people most. We never sell your data. Read our{" "}
                            <a href="/privacy" className="text-lodestar-gold-bright underline">Privacy Policy</a>.
                        </p>
                    </div>
                    {/* Buttons — compact pills on mobile so they fit on the same row. */}
                    <div className="flex items-center gap-2 sm:hidden shrink-0">
                        <button
                            type="button"
                            data-testid="cookie-accept"
                            onClick={accept}
                            className="px-3 py-1.5 rounded-full text-[12px] font-medium
                                       bg-gradient-to-r from-lodestar-gold-royal to-lodestar-gold-bright
                                       text-lodestar-plum-deep hover:opacity-90 transition-opacity"
                        >
                            Accept
                        </button>
                        <button
                            type="button"
                            data-testid="cookie-decline"
                            onClick={decline}
                            className="px-3 py-1.5 rounded-full text-[12px] text-lodestar-white-mist/80
                                       border border-lodestar-gold-soft/30 hover:text-lodestar-white-pure hover:border-lodestar-gold-soft/50 transition-colors"
                        >
                            Reject
                        </button>
                    </div>
                    {/* Dismiss (X) — desktop only; mobile relies on Accept/Reject inline. */}
                    <button
                        type="button"
                        aria-label="Dismiss"
                        data-testid="cookie-dismiss"
                        onClick={decline}
                        className="hidden sm:block text-lodestar-white-mist/55 hover:text-lodestar-white-pure transition-colors -mr-1 -mt-1"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {/* Desktop-only action row */}
                <div className="hidden sm:flex mt-4 flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={accept}
                        className="px-4 py-2 rounded-full text-[13px] font-medium
                                   bg-gradient-to-r from-lodestar-gold-royal to-lodestar-gold-bright
                                   text-lodestar-plum-deep hover:opacity-90 transition-opacity"
                    >
                        Accept analytics
                    </button>
                    <button
                        type="button"
                        onClick={decline}
                        className="px-4 py-2 rounded-full text-[13px] text-lodestar-white-mist/80
                                   border border-lodestar-gold-soft/30 hover:text-lodestar-white-pure hover:border-lodestar-gold-soft/50 transition-colors"
                    >
                        Reject
                    </button>
                </div>
            </div>
        </div>
    );
}
